// Enum values matching the backend API
export enum ProjectStatus {
  Active = 0,
  OnHold = 1,
  Cancelled = 2,
  Done = 3,
}

export enum ProjectPhase {
  InitialReview = 0,
  DueDiligence = 1,
  Negotiation = 2,
  Closing = 3,
}

export enum RiskLevel {
  Low = 0,
  Medium = 1,
  High = 2,
}

export const ProjectStatusLabels: Record<ProjectStatus, string> = {
  [ProjectStatus.Active]: "Active",
  [ProjectStatus.OnHold]: "On Hold",
  [ProjectStatus.Cancelled]: "Cancelled",
  [ProjectStatus.Done]: "Done",
};

export const ProjectPhaseLabels: Record<ProjectPhase, string> = {
  [ProjectPhase.InitialReview]: "Initial Review",
  [ProjectPhase.DueDiligence]: "Technical Assessment",
  [ProjectPhase.Negotiation]: "Reporting",
  [ProjectPhase.Closing]: "Sign-off",
};

export const RiskLevelLabels: Record<RiskLevel, string> = {
  [RiskLevel.Low]: "Low",
  [RiskLevel.Medium]: "Medium",
  [RiskLevel.High]: "High",
};

export enum Sector {
  Technology = 0,
  Fintech = 1,
  Healthcare = 2,
  Manufacturing = 3,
  Energy = 4,
  RealEstate = 5,
  Retail = 6,
  Media = 7,
  Telecom = 8,
  Education = 9,
  Government = 10,
  Logistics = 11,
  ProfessionalServices = 12,
}

export enum DealType {
  Acquisition = 0,
  Merger = 1,
  GrowthInvestment = 2,
  PEBuyout = 3,
  CarveOut = 4,
  JointVenture = 5,
  AssetPurchase = 6,
}

export const SectorLabels: Record<Sector, string> = {
  [Sector.Technology]: "Technology / SaaS",
  [Sector.Fintech]: "Fintech",
  [Sector.Healthcare]: "Healthcare",
  [Sector.Manufacturing]: "Manufacturing",
  [Sector.Energy]: "Energy",
  [Sector.RealEstate]: "Real Estate",
  [Sector.Retail]: "Retail / E-commerce",
  [Sector.Media]: "Media & Entertainment",
  [Sector.Telecom]: "Telecom",
  [Sector.Education]: "Education",
  [Sector.Government]: "Government",
  [Sector.Logistics]: "Logistics",
  [Sector.ProfessionalServices]: "Professional Services",
};

export const DealTypeLabels: Record<DealType, string> = {
  [DealType.Acquisition]: "Acquisition",
  [DealType.Merger]: "Merger",
  [DealType.GrowthInvestment]: "Growth Investment",
  [DealType.PEBuyout]: "PE Buyout",
  [DealType.CarveOut]: "Carve-Out",
  [DealType.JointVenture]: "Joint Venture",
  [DealType.AssetPurchase]: "Asset Purchase",
};

// Generic paginated response envelope from the API
export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export enum ReportGenerationStatus {
  Default = 0,
  Generating = 1,
  Generated = 2,
}

// API response type for a project
export interface Project {
  id: number;
  name: string;
  targetName: string;
  status: ProjectStatus | null;
  phase: ProjectPhase | null;
  expectedClose: string | null;
  risk: RiskLevel | null;
  sector: Sector | null;
  dealType: DealType | null;
  organizationId: number;
  organizationName: string;
  estimatedCreditCost: number;
  redacted: string | null;
  owner: string | null;
  reportGenerationStatus: ReportGenerationStatus;
  reportGenerationStatusChangedAt?: string | null;
  rowVersion: number;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Create project command
export interface CreateProjectCommand {
  name: string;
  targetName: string;
  organizationId: number;
  status?: ProjectStatus | null;
  phase?: ProjectPhase | null;
  expectedClose?: string | null;
  sector?: Sector | null;
  dealType?: DealType | null;
  redacted: string;
  owner?: string | null;
}

// Update project command
export interface UpdateProjectCommand {
  id: number;
  name: string;
  targetName: string;
  status?: ProjectStatus | null;
  phase?: ProjectPhase | null;
  expectedClose?: string | null;
  sector?: Sector | null;
  dealType?: DealType | null;
  redacted: string;
  owner?: string | null;
  rowVersion: number;
  updatedBy: string;
}

export interface DeleteProjectCommand {
  id: number;
  rowVersion: number;
}

// Role type values matching backend RoleType enum
export enum RoleType {
  Owner = 0,
  Editor = 1,
  Viewer = 2,
}

// API response for a role
export interface Role {
  id: number;
  roleType: RoleType;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

// API response for a project-user association
export interface ProjectUser {
  id: number;
  projectId: number;
  userId: number;
  userEmail: string;
  userNickName: string | null;
  roleId: number;
  roleType: RoleType;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}
